import { Currency, Game, GameInfoFlags, GameMode, Market, Odd } from '@arland-bmnext/api-data'
import {
  faCaretDown,
  faCaretUp,
  faExclamationCircle,
  faInfoCircle,
  faLockKeyhole
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useTranslation from 'next-translate/useTranslation'
import React, { useMemo, useState } from 'react'
import { useCommonContext } from '../../context/common.context'
import { getCurrencySymbol } from '../../util/currency'
import { formatAdvancedDate } from '../../util/date'
import { getGameCompetitors } from '../../util/game'
import SportIcon from './SportIcon'
import Link from 'next/link'
import { BaseOddsButton, OddsButtonProps } from './BaseOddsButton'
import CountryIcon from '../core/CountryIcon'
import { useGamePeriods } from '../../lib/odds'
import GameTime from './GameTime'
import ServeIndicator from './ServeIndicator'
import useNumberChange from '../../hooks/useNumberChange'
import Jersey from './Jersey'
import Highlighter from 'react-highlight-words'
import { buildEventUrl, buildLeagueUrl } from '../../util/url'
import useUserCurrency from '../../hooks/useUserCurrency'
import useCurrentLanguage from '../../hooks/useCurrentLanguage'
import {
  useLiveQuickMarkets,
  useMarketTypeTranslations,
  useQuickMarkets,
  useSportsBettingSettings
} from '../../lib/content'
import {
  MarketTypeOutcomeSpecification,
  MarketTypeSpecification,
  marketTypeSpecifications
} from '../../util/market-type-specifications'
import Tag, { BetBuilderTag } from '../core/Tag'
import { useRouter } from 'next/router'
import HelpText from '../HelpText'
import { EventMarketOdd } from '../../pages/sports/event/[eventId]'
import { Swiper, SwiperSlide } from 'swiper/react'
import { BetTypes } from '../../services/bet/bet.models'
import {
  getIntlAmericanOddNumberFormat,
  getIntlDecimalOddNumberFormat,
  getIntlPotentialWinningOddNumberFormat
} from '../../util/internationalization'
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons'
import { getMarketTypeTranslationWithGameData } from '../../util/market'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import SportCountryIcon from './SportCountryIcon'
import { useLiveContext } from '../../context/live.context'
import { Constants } from '../../util/constants'
import { PotentialWinningsCurrencyFormatDTO } from '@arland-bmnext/webapps-api-data'
import { OddValueFormat } from '../../hooks/useOddsFormat'
import { getMostBalancedLineForMarkets } from '../../util/odds'
import { sortArray } from '../../util/array'

const isEqual = require('react-fast-compare')

const styles = {
  twoWayOddWidth: 'w-[calc((var(--odd-size)*3+(var(--odd-gap)*2)-var(--odd-gap))/2)]',
  gameMarketsWidth: 'w-[max(calc(var(--odd-size)*3+(var(--odd-gap)*8)+8px),_calc(100%-225px))]',
  gameDetailsWidth: 'w-[calc(100%-(var(--odd-size)*3+(var(--odd-gap)*8)+8px))] max-w-[225px]'
}

const deepEqualityCheck = (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps)
}

export const GamesListSkeleton = ({ gamesCount = 5, marketsCount = 5 }) => {
  return (
    <div className="games-list-skeleton flex flex-col w-full shadow-card-soft md:shadow-none skeleton md:rounded-md">
      <div className="flex flex-row justify-between items-end w-full py-2 bg-card">
        <div className={`flex flex-col justify-between h-full pl-3 md:pl-4 ${styles.gameDetailsWidth}`}>
          <div className="flex flex-col space-y-2">
            {[...Array(gamesCount)]?.map((_, index) => {
              return (
                <div
                  className="flex flex-col flex-grow justify-evenly whitespace-nowrap overflow-hidden h-[64.5px] pb-2 border-b-[.5px] border-neutral last:border-none last:h-[63.5px]"
                  key={index}
                >
                  <div className="flex justify-between space-x-2">
                    <div className="h-3 w-24 skeleton-content"></div>
                  </div>
                  <div className="flex justify-between space-x-2 text-xs">
                    <div className="h-3 w-28 skeleton-content"></div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className={`flex overflow-hidden  ${styles.gameMarketsWidth}`}>
          {[...Array(marketsCount)]?.map((_, index) => {
            return (
              <div className="flex flex-col" key={index}>
                <div className="flex flex-col">
                  <div className="flex items-end justify-center opacity-70 h-[20px]">
                    <div className="w-24 h-[10px] skeleton-content"></div>
                  </div>
                  <div className="flex items-center justify-center space-x-[var(--odd-gap)] opacity-50 h-[20px]">
                    {[...Array(3)].map((_, index) => (
                      <span key={index} className={`flex justify-center whitespace-nowrap w-odd  `}>
                        <div className="h-[10px] w-6 skeleton-content"></div>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {[...Array(gamesCount)]?.map((_, index) => {
                    return (
                      <div
                        className="flex flex-row space-x-[var(--odd-gap)] justify-between items-center px-[calc(var(--odd-gap)*2)] pb-2 h-[64.5px] last:h-[63.5px] border-b-[.5px] border-neutral last:border-none"
                        key={index}
                      >
                        {[...Array(3)].map((_, index) => (
                          <span
                            key={index}
                            className={`flex w-odd h-odd justify-center items-center skeleton-content rounded-[var(--odd-radius)]`}
                          ></span>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export const OutrightsMarket = ({
                                  market,
                                  game,
                                  currency,
                                  showOnlyFirstNOdds = 0,
                                  showOnlyFirstNOddsThreshold = 2,
                                  toggleCollapsedClick = null
                                }) => {
  const { t } = useTranslation('common')
  const [collapsed, setCollapsed] = useState(
    showOnlyFirstNOdds > 0 && market.odds.length > showOnlyFirstNOdds + showOnlyFirstNOddsThreshold
  )

  const sortedOdds = sortArray(market.odds, 'value', 'asc') ?? []
  const odds = collapsed ? sortedOdds?.slice(0, showOnlyFirstNOdds) : sortedOdds

  const language = useCurrentLanguage()
  const marketTypeTranslations = useMarketTypeTranslations(language?.id)
  const marketTypeTranslation = getMarketTypeTranslationWithGameData(game, market, marketTypeTranslations)

  const onToggleCollapsedClick = () => {
    if (toggleCollapsedClick) toggleCollapsedClick()
    else setCollapsed(!collapsed)
  }

  return (
    <div
      className="games-list-outrights-market flex flex-col space-y-2 pb-4 last:pb-0 border-b-[.5px] border-neutral last:border-b-0">
      <div className="games-list-outrights-market-details flex space-x-2 items-center mb-2">
        <span className="games-list-outrights-market-details-name text-xs font-bold">{market.name}</span>
        {marketTypeTranslation?.description && (
          <HelpText title={marketTypeTranslation.name} description={marketTypeTranslation.description} />
        )}
      </div>

      <div className="games-list-outrights-market-outcomes flex flex-col space-y-2">
        {odds?.map((odd) => (
          <BaseOddsButton
            currency={currency}
            odd={odd}
            market={market}
            game={game}
            OddsButton={EventMarketOdd}
            key={odd.id}
          />
        ))}

        {showOnlyFirstNOdds > 0 && market.odds.length > showOnlyFirstNOdds + showOnlyFirstNOddsThreshold && (
          <div
            className="games-list-outrights-market-outcomes-toggle flex space-x-1 w-full py-2 justify-center items-center text-primary text-sm font-medium cursor-pointer hover:underline"
            onClick={onToggleCollapsedClick}
          >
            <span>{collapsed ? t('Common.showMore') : t('Common.collapse')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export const OutrightsList = ({ games, currency, className = '' }) => {
  const { t } = useTranslation('bets')
  const router = useRouter()

  const onNavigateToEvent = (game) => {
    router.push(`/sports/event/${game.id}`)
  }

  return (
    <div
      className={`games-list-outrights flex flex-col w-full bg-card text-cardContrast border-t-[1px] border-neutral px-3 md:px-4 py-4 pt-2 ${className}`}
    >
      <div className="games-list-outrights-header flex items-center overflow-hidden h-[40px]">
        <span
          className="games-list-outrights-header-label overflow-hidden text-ellipsis whitespace-nowrap font-bold text-xs opacity-80">
          {t('GamesList.outrightEvents')}
        </span>
      </div>

      <div className="games-list-outrights-items flex flex-col space-y-2 w-full">
        {games?.map((game: Game) => (
          <div className="games-list-outrights-item flex flex-col space-y-4" key={game.id}>
            <Link href={`/sports/event/${game.id}`} legacyBehavior>
              <div
                className="games-list-outrights-item-inner flex space-x-2 bg-odd text-oddContrast border-[1px] border-oddBorder w-full rounded-md p-4 cursor-pointer">
                <div className="games-list-outrights-item-details flex flex-col flex-grow space-y-1 hover:underline">
                  <span className="games-list-outrights-item-details-name text-sm font-semibold">{game.name}</span>
                  <span className="games-list-outrights-item-details-date text-xs opacity-80">
                    {formatAdvancedDate(t, game.startDate)}
                  </span>
                </div>

                <div className="games-list-outrights-item-indicator flex space-x-2 flex-shrink-0 items-center">
                  <Tag
                    backgroundColor="bg-primary"
                    textColor="text-primaryContrast"
                    className="!px-2 !rounded-full !text-xs"
                  >
                    {t('GamesList.nBets', { betCount: game.openMarketCount })}
                  </Tag>
                  <FontAwesomeIcon icon={faChevronRight} />
                </div>
              </div>
            </Link>

            {game.markets[0] && (
              <OutrightsMarket
                game={game}
                market={game.markets[0]}
                currency={currency}
                showOnlyFirstNOdds={3}
                toggleCollapsedClick={() => onNavigateToEvent(game)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const GameListItemOdd = ({
                           getStylesByOddState,
                           isTwoWay,
                           onToggleOdd,
                           oddValueChange,
                           oddDisplayValue,
                           oddDisplayValuePostFix,
                           oddHasError,
                           oddState,
                           displayMode,
                           currency,
                           betType
                         }: OddsButtonProps) => {
  const sportsBettingSettings = useSportsBettingSettings()

  return (
    <div
      className={`games-list-odd flex flex-col h-odd relative ${
        isTwoWay ? styles.twoWayOddWidth : 'w-odd'
      } rounded-[var(--odd-radius)] border-[1px] justify-between whitespace-nowrap overflow-hidden transition-all duration-300 hover:bg-gradient-radial-to-tr ${getStylesByOddState()}`}
      onClick={onToggleOdd}
    >
      {oddValueChange > 0 && (
        <FontAwesomeIcon
          className="games-list-odd-change-up absolute right-0 top-0 text-green-600 rotate-45"
          icon={faCaretUp}
        />
      )}

      {oddValueChange < 0 && (
        <FontAwesomeIcon
          className="games-list-odd-change-down absolute bottom-0 left-0 text-red-600 rotate-45"
          icon={faCaretDown}
        />
      )}

      <div
        className="games-list-odd-inner flex flex-col justify-center space-y-1 items-center font-normal w-full h-full">
        {!oddHasError && oddState !== 'checking' && (
          <>
            <span className="games-list-odd-value text-xs font-medium leading-[10px]">
              {displayMode === OddValueFormat.Fractional ? (
                oddDisplayValue
              ) : (
                <>
                  {oddDisplayValue != null && oddDisplayValue != '' && !isNaN(oddDisplayValue as number) && (
                    <>
                      {displayMode === OddValueFormat.PotentialWinnings &&
                        getIntlPotentialWinningOddNumberFormat(oddDisplayValue as number)}

                      {displayMode === OddValueFormat.DecimalOdds &&
                        getIntlDecimalOddNumberFormat(oddDisplayValue as number)}

                      {displayMode === OddValueFormat.AmericanOdds &&
                        getIntlAmericanOddNumberFormat(oddDisplayValue as number)}

                      {displayMode === OddValueFormat.HongKongOdds &&
                        getIntlDecimalOddNumberFormat(oddDisplayValue as number)}
                    </>
                  )}
                </>
              )}

              {oddDisplayValuePostFix}
            </span>

            {displayMode === OddValueFormat.PotentialWinnings && betType != BetTypes.System && (
              <span className="games-list-odd-currency text-xxs leading-3 opacity-60">
                {sportsBettingSettings?.oddsDisplay?.oddsFormat?.potentialWinningsCurrencyFormat ===
                PotentialWinningsCurrencyFormatDTO.ShortSign
                  ? getCurrencySymbol(currency, 'shortSign')
                  : getCurrencySymbol(currency, 'symbol')}
              </span>
            )}
          </>
        )}

        {oddHasError && <FontAwesomeIcon icon={faExclamationCircle} />}

        {oddState === 'checking' && (
          <FontAwesomeIcon icon={faSpinner} className="games-list-odd-checking animate-spin" />
        )}
      </div>
    </div>
  )
}

const DummyOdd = ({ isTwoWay }) => {
  return (
    <div
      className={`games-list-dummy-odd ${
        isTwoWay ? styles.twoWayOddWidth : 'w-odd'
      } flex h-odd justify-center items-center bg-odd text-oddContrast rounded-[var(--odd-radius)] border-[1px] border-oddBorder`}
    >
      <FontAwesomeIcon icon={faLockKeyhole} className="games-list-dummy-odd-icon opacity-50" />
    </div>
  )
}

const SpecialValue = ({ specialValue }) => {
  return (
    <div className="games-list-market-special-value w-odd h-odd flex justify-center items-center">
      <span className="text-xs font-semibold">{specialValue}</span>
    </div>
  )
}

type GameListItemMarketProps = {
  currency: Currency
  game: Game
  market: Market
  sportName: string
  sportShortSign: string
  leagueName: string
  marketTypeSpecification: MarketTypeSpecification
}
const GameListItemMarket = ({
                              currency,
                              market,
                              game,
                              sportName,
                              sportShortSign,
                              leagueName,
                              marketTypeSpecification
                            }: GameListItemMarketProps) => {
  const isTwoWay = marketTypeSpecification.outcomes.length === 2

  const findOddForSpecification = (outcomeSpecification: MarketTypeOutcomeSpecification): Odd => {
    return market?.odds?.find((odd) => outcomeSpecification?.oddTemplateIds?.includes(odd.oddTemplateId))
  }

  return (
    <div
      className="games-list-item-market flex flex-row space-x-[var(--odd-gap)] justify-between items-center px-[calc(var(--odd-gap)*2)] pb-2 h-[64.5px] last:h-[63.5px] border-b-[.5px] border-neutral last:border-none">
      {marketTypeSpecification.outcomes.map((mts, index) => {
        const odd = findOddForSpecification(mts)
        const specialValue = mts.specialValueName && market ? market[mts.specialValueName] : undefined

        return (
          <React.Fragment key={odd ? odd.id : index}>
            {odd ? (
              <BaseOddsButton
                currency={currency}
                odd={odd}
                market={market}
                game={game}
                sportName={sportName}
                sportShortSign={sportShortSign}
                leagueName={leagueName}
                isTwoWay={isTwoWay}
                OddsButton={GameListItemOdd}
              />
            ) : (
              <>{specialValue ? <SpecialValue specialValue={specialValue} /> : <DummyOdd isTwoWay={isTwoWay} />}</>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}
const GameListItemMarketMemo = React.memo(GameListItemMarket, deepEqualityCheck)

const GamesListGameScoreItem = ({ scoreChanged, score, isTotalScore = false }) => {
  return (
    <div
      className={`games-list-game-score-item flex justify-center items-center shrink-0 h-4 min-w-4 p-1 text-xxs rounded-sm ${
        isTotalScore ? 'games-list-game-score-item-total font-bold bg-gray-500 bg-opacity-10' : ''
      } ${scoreChanged ? 'games-list-game-score-item-change bg-live text-liveContrast' : 'text-inherit'}`}
    >
      <span className={`games-list-game-score-item-score flex-shrink-0 font-mono transition-all duration-500`}>
        {score}
      </span>
    </div>
  )
}

const GamesListGameScore = ({ game }) => {
  const liveContext = useLiveContext()
  const language = useCurrentLanguage()
  const gamePeriods = useGamePeriods(language?.id)
  const periodScores = game?.liveStatus?.periodScores
  const currentPeriod = gamePeriods?.find((gamePeriod) => gamePeriod.id === game?.liveStatus?.currentPeriodId)
  const currentPeriodScore = periodScores?.find((periodScore) => periodScore.periodId === currentPeriod?.id)
  const sportShortSign = liveContext.sports?.find((sport) => sport.id === game.sportId)?.shortSign
  const currentServer = game?.liveStatus?.currentServer

  const [homeScore, homeScoreChanged] = useNumberChange(game?.liveStatus?.homeScore)
  const [awayScore, awayScoreChanged] = useNumberChange(game?.liveStatus?.awayScore)

  const [periodScoreHome, periodScoreHomeChanged] = useNumberChange(currentPeriodScore?.homeScore)
  const [periodScoreAway, periodScoreAwayChanged] = useNumberChange(currentPeriodScore?.awayScore)

  const [gameScoreHome, gameScoreHomeChanged] = useNumberChange(game?.liveStatus?.gameScoreHome)
  const [gameScoreAway, gameScoreAwayChanged] = useNumberChange(game?.liveStatus?.gameScoreAway)

  return (
    <div className="games-list-game-score flex space-x-1 pr-1">
      <div className="games-list-game-score-total flex grow flex-col justify-evenly items-stretch">
        <div className="flex items-center h-4"></div>
        <GamesListGameScoreItem score={game?.usesUsDisplay ? awayScore : homeScore}
                                scoreChanged={game?.usesUsDisplay ? awayScoreChanged : homeScoreChanged} isTotalScore />
        <GamesListGameScoreItem score={game?.usesUsDisplay ? homeScore : awayScore}
                                scoreChanged={game?.usesUsDisplay ? homeScoreChanged : awayScoreChanged} isTotalScore />
      </div>

      {sportShortSign != 'fu' && sportShortSign != 'ba' && currentPeriodScore != null && (
        <>
          <div className="games-list-game-score-period flex grow flex-col justify-evenly items-stretch">
            <div className="flex items-center h-4"></div>
            <GamesListGameScoreItem score={game?.usesUsDisplay ? periodScoreAway : periodScoreHome}
                                    scoreChanged={game?.usesUsDisplay ? periodScoreAwayChanged : periodScoreHomeChanged} />
            <GamesListGameScoreItem score={game?.usesUsDisplay ? periodScoreHome : periodScoreAway}
                                    scoreChanged={game?.usesUsDisplay ? periodScoreHomeChanged : periodScoreAwayChanged} />
          </div>

          {sportShortSign == 'te' && gameScoreHome != null && gameScoreAway != null && (
            <div className="games-list-game-score-game flex grow flex-col justify-evenly items-stretch">
              <div className="flex h-4"></div>
              <GamesListGameScoreItem
                score={game?.usesUsDisplay ? (gameScoreAway == 50 ? 'A' : gameScoreAway) : (gameScoreHome == 50 ? 'A' : gameScoreHome)}
                scoreChanged={game?.usesUsDisplay ? gameScoreAwayChanged : gameScoreHomeChanged}
              />
              <GamesListGameScoreItem
                score={game?.usesUsDisplay ? (gameScoreHome == 50 ? 'A' : gameScoreHome) : (gameScoreAway == 50 ? 'A' : gameScoreAway)}
                scoreChanged={game?.usesUsDisplay ? gameScoreHomeChanged : gameScoreAwayChanged}
              />
            </div>
          )}

          {currentServer != 0 && (
            <div className="games-list-game-score-current-server flex grow flex-col justify-evenly items-stretch">
              <div className="flex h-4"></div>
              <div className="flex items-center h-4">
                <ServeIndicator isServer={game?.usesUsDisplay ? currentServer === 2 : currentServer === 1} />
              </div>
              <div className="flex items-center h-4">
                <ServeIndicator isServer={game?.usesUsDisplay ? currentServer === 1 : currentServer === 2} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

type GamesListGameDetailsItemProps = {
  game: Game
  highlightedTerm?: string
}
const GamesListGameDetailsItem = ({ game, highlightedTerm = '' }: GamesListGameDetailsItemProps) => {
  const { t, lang } = useTranslation()
  const commonContext = useCommonContext()
  const sportsBettingSettings = useSportsBettingSettings()

  const language = commonContext.getLanguageByShortSign(lang)
  const gamePeriods = useGamePeriods(language?.id)

  const competitors = getGameCompetitors(game)
  const comp1Jersey = game?.competitors[0]?.competitor?.teamProfile?.jerseys[0]
  const comp2Jersey = game?.competitors[1]?.competitor?.teamProfile?.jerseys[0]
  const displayJersey = comp1Jersey && comp2Jersey && !Constants.LeaguesWithFlags.includes(game.leagueId)

  const comp1Country =
    commonContext.countries?.find(
      (c) =>
        c.id === game?.competitors[0]?.competitor?.countryId ||
        c['isoAlpha3'] === game?.competitors[0]?.competitor?.countryCode
    )?.shortSign ?? game?.competitors[0]?.competitor?.countryCode

  const comp2Country =
    commonContext.countries?.find(
      (c) =>
        c.id === game?.competitors[1]?.competitor?.countryId ||
        c['isoAlpha3'] === game?.competitors[1]?.competitor?.countryCode
    )?.shortSign ?? game?.competitors[1]?.competitor?.countryCode

  const displayCountry = comp1Country && comp2Country && !displayJersey

  const isLive = game?.mode == GameMode.Live
  const currentPeriod = gamePeriods?.find((gamePeriod) => gamePeriod.id === game?.liveStatus?.currentPeriodId)
  const gameTime = game?.liveStatus?.gameTime

  const [, homeScoreChanged] = useNumberChange(game?.liveStatus?.homeScore)
  const [, awayScoreChanged] = useNumberChange(game?.liveStatus?.awayScore)

  return (
    <Link href={buildEventUrl(game.id, game.name, game.usesUsDisplay)} legacyBehavior>
      <div
        className="games-list-game-details-item flex flex-grow space-x-2 whitespace-nowrap overflow-hidden h-[64.5px] pb-2 border-b-[.5px] border-neutral last:border-none last:h-[63.5px] cursor-pointer"
        data-gid={game.id}
      >
        <div className="games-list-game-details-item-inner flex grow flex-col justify-evenly overflow-hidden">
          <div className="games-list-game-details-item-details flex justify-between space-x-1 items-center">
            {isLive ? (
              <div
                className="games-list-game-details-item-details-live flex space-x-2 overflow-hidden overflow-ellipsis text-xxs">
                {gameTime && currentPeriod?.period != -1 && (
                  <span
                    className="games-list-game-details-item-details-live-game-time-wrapper text-live font-bold font-mono">
                    <GameTime gameTime={gameTime} currentPeriod={currentPeriod} />
                  </span>
                )}
                {currentPeriod && (
                  <span className="games-list-game-details-item-details-live-period opacity-60">
                    {currentPeriod?.name}
                  </span>
                )}
              </div>
            ) : (
              <div className="games-list-game-details-item-details-prematch flex space-x-1">
                {(game.infoFlags & GameInfoFlags.BetBuilder) === GameInfoFlags.BetBuilder &&
                  sportsBettingSettings?.sportCast?.betBuilder?.enabled && <BetBuilderTag useShortSign />}
                <span className="games-list-game-details-item-details-prematch-startdate opacity-60 text-xxs">
                  {formatAdvancedDate(t, game.startDate)}
                </span>
              </div>
            )}
            <div id={'sir-betassist-' + game.id}></div>
          </div>

          <div className="games-list-game-details-item-competitors hover:underline">
            <div
              className={`games-list-game-details-item-competitor flex justify-between space-x-2 text-xs transition-all duration-500 ${
                (game?.usesUsDisplay ? awayScoreChanged : homeScoreChanged)
                  ? 'games-list-game-details-item-competitor-score-change bg-live text-liveContrast'
                  : 'bg-transparent'
              }`}
            >
              <div
                className="games-list-game-details-item-competitor-inner flex space-x-1 items-center overflow-hidden">
                {displayJersey && (
                  <div className="games-list-game-details-item-competitor-jersey h-4 w-4">
                    {game?.usesUsDisplay ? <Jersey{...comp2Jersey} /> : <Jersey {...comp1Jersey} />}
                  </div>
                )}

                {displayCountry && (
                  <div className="games-list-game-details-item-competitor-country h-3 w-4 px-[2px]">
                    <CountryIcon countryCode={game?.usesUsDisplay ? comp2Country : comp1Country} />
                  </div>
                )}

                <span
                  className="games-list-game-details-item-competitor-name overflow-hidden overflow-ellipsis whitespace-nowrap">
                  {competitors?.length > 0 ? (
                    <Highlighter
                      highlightClassName="text-primary bg-transparent"
                      searchWords={[highlightedTerm]}
                      autoEscape={true}
                      textToHighlight={game?.usesUsDisplay ? competitors[1] : competitors[0]}
                    />
                  ) : (
                    ''
                  )}
                </span>
              </div>
            </div>

            <div
              className={`games-list-game-details-item-competitor flex justify-between space-x-2 text-xs transition-all duration-500 ${
                (game?.usesUsDisplay ? homeScoreChanged : awayScoreChanged)
                  ? 'games-list-game-details-item-competitor-score-change bg-live text-liveContrast'
                  : 'bg-transparent'
              }`}
            >
              <div
                className="games-list-game-details-item-competitor-inner flex space-x-1 items-center overflow-hidden">
                {displayJersey && (
                  <div className="games-list-game-details-item-competitor-jersey h-4 w-4">
                    {game?.usesUsDisplay ? <Jersey {...comp1Jersey} /> : <Jersey {...comp2Jersey} />}
                  </div>
                )}

                {displayCountry && (
                  <div className="games-list-game-details-item-competitor-country h-3 w-4 px-[2px]">
                    <CountryIcon countryCode={game?.usesUsDisplay ? comp1Country : comp2Country} />
                  </div>
                )}

                <span
                  className="games-list-game-details-item-competitor-name overflow-hidden overflow-ellipsis whitespace-nowrap">
                  {competitors?.length > 1 ? (
                    <Highlighter
                      highlightClassName="text-primary bg-transparent"
                      searchWords={[highlightedTerm]}
                      autoEscape={true}
                      textToHighlight={game?.usesUsDisplay ? competitors[0] : competitors[1]}
                    />
                  ) : (
                    ''
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isLive && <GamesListGameScore game={game} />}
      </div>
    </Link>
  )
}
const GamesListGameDetailsItemMemo = React.memo(GamesListGameDetailsItem, deepEqualityCheck)

type GamesListGameDetailsHeaderProps = {
  leagueName?: string
  leagueId?: number
  deepLinkLeague?: boolean
  sportShortSign?: string
  countryCode?: string
  iconType?: 'sport' | 'country' | 'sport-country' | 'none'
  isLiveSection?: boolean
  titleType?: 'sectionName' | 'leagueName'
  displayTitle?: boolean
}
const GamesListGameDetailsHeader = (props: GamesListGameDetailsHeaderProps) => {
  const { t } = useTranslation('bets')

  return (
    <>
      {props.titleType === 'leagueName' ? (
        <div className="games-list-game-details-header flex overflow-hidden -ml-2 h-[40px]">
          {props.displayTitle && (props?.leagueName || props.sportShortSign) && (
            <div
              className="games-list-game-details-header-title flex items-center space-x-1 whitespace-nowrap overflow-hidden px-2">
              {props.iconType == 'sport' && (
                <div className="games-list-game-details-header-title-sport-icon h-5 w-5">
                  <SportIcon sportShortSign={props.sportShortSign} />
                </div>
              )}
              {props.iconType == 'country' && (
                <div className="games-list-game-details-header-title-country-icon h-5 w-5">
                  <CountryIcon countryCode={props.countryCode} />
                </div>
              )}
              {props.iconType == 'sport-country' && (
                <SportCountryIcon
                  countryCode={props.countryCode}
                  sportShortSign={props.sportShortSign}
                  height="16px"
                  width="16px"
                  sportIconBackground="bg-card"
                />
              )}
              {props?.leagueName && (
                <span
                  className={`games-list-game-details-header-title-league-name overflow-hidden text-ellipsis font-bold text-xs opacity-80 ${props.deepLinkLeague && props.leagueId ? 'hover:opacity-100' : ''}`}
                >
                  {props.deepLinkLeague && props.leagueId && (
                    <Link href={buildLeagueUrl(props.leagueId, props.leagueName)} legacyBehavior>
                      {props.leagueName}
                    </Link>
                  )}
                  {!props.deepLinkLeague && props.leagueName}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center overflow-hidden h-[40px]">
          {props.displayTitle && (
            <span className="overflow-hidden text-ellipsis whitespace-nowrap font-bold text-xs opacity-80">
              {props.isLiveSection ? t('GamesList.liveEvents') : t('GamesList.prematchEvents')}
            </span>
          )}
        </div>
      )}
    </>
  )
}

type GamesListGameDetailsProps = {
  games: Game[]
  leagueName?: string
  leagueId?: number
  deepLinkLeague?: boolean
  sportShortSign?: string
  displayGamesWithNoMarkets?: boolean
  countryCode?: string
  iconType?: 'sport' | 'country' | 'sport-country' | 'none'
  isLiveSection?: boolean
  titleType?: 'sectionName' | 'leagueName'
  highlightedTerm?: string
  repeatHeaderAfterNRows?: number
}
const GamesListGameDetails = ({
                                games,
                                leagueName,
                                leagueId,
                                deepLinkLeague,
                                sportShortSign,
                                displayGamesWithNoMarkets,
                                countryCode,
                                iconType,
                                isLiveSection = false,
                                titleType,
                                highlightedTerm = '',
                                repeatHeaderAfterNRows
                              }: GamesListGameDetailsProps) => {
  const _games = displayGamesWithNoMarkets ? games : games.filter((game) => game?.markets?.length > 0)
  const headerProps = {
    leagueName,
    leagueId,
    deepLinkLeague,
    sportShortSign,
    countryCode,
    iconType,
    isLiveSection,
    titleType
  }

  return (
    <div
      className={`games-list-game-details flex flex-col justify-between h-full pl-3 md:pl-4 ${
        isLiveSection ? 'games-list-game-details-live border-l-4 border-live !pl-2 md:!pl-3' : ''
      } ${styles.gameDetailsWidth}`}
    >
      <GamesListGameDetailsHeader {...headerProps} displayTitle={true} />

      <div className="games-list-game-details-items flex flex-col space-y-2">
        {_games?.map((game, index) => {
          return (
            <React.Fragment key={game.id}>
              {repeatHeaderAfterNRows != 0 && index > 0 && index % repeatHeaderAfterNRows === 0 && (
                <GamesListGameDetailsHeader {...headerProps} displayTitle={false} />
              )}
              <GamesListGameDetailsItemMemo game={game} highlightedTerm={highlightedTerm} />
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

type GamesListGameMarketsColumnHeaderProps = {
  marketTypeSpecification: MarketTypeSpecification
}
const GamesListGameMarketsColumnHeader = ({ marketTypeSpecification }: GamesListGameMarketsColumnHeaderProps) => {
  const language = useCurrentLanguage()
  const isTwoWay = marketTypeSpecification.outcomes.length === 2

  const getTranslatedMarketName = () => {
    const translation = marketTypeSpecification?.translations?.find((x) => x.languageId === language?.id)
    return translation ? translation.name : marketTypeSpecification.name
  }

  const getTranslatedOutcomeName = (outcome) => {
    const translation = outcome?.translations?.find((x) => x.languageId === language?.id)
    return translation ? translation.name : outcome.name
  }

  return (
    <div className="games-list-game-markets-column-header flex flex-col">
      <span
        className="games-list-game-markets-column-header-market flex items-end justify-center text-xxs font-medium opacity-70 h-[20px]">
        {getTranslatedMarketName()}
      </span>
      <div
        className="games-list-game-markets-column-header-outcomes flex items-start justify-center space-x-[var(--odd-gap)] opacity-50 text-xxs h-[20px]">
        {marketTypeSpecification.outcomes.map((outcome, index) => (
          <span
            key={index}
            className={`games-list-game-markets-column-header-outcome flex justify-center whitespace-nowrap ${
              isTwoWay ? styles.twoWayOddWidth : 'w-odd'
            }`}
          >
            {getTranslatedOutcomeName(outcome)}
          </span>
        ))}
      </div>
    </div>
  )
}

type GamesListGameMarketsItemProps = {
  currency: Currency
  games?: Game[]
  leagueName?: string
  sportName?: string
  sportShortSign?: string
  marketTypeSpecification: MarketTypeSpecification
  repeatHeaderAfterNRows?: number
}
const GamesListGameMarketsColumn = ({
                                      currency,
                                      games,
                                      leagueName,
                                      sportName,
                                      sportShortSign,
                                      marketTypeSpecification,
                                      repeatHeaderAfterNRows
                                    }: GamesListGameMarketsItemProps) => {
  const getMarket = (game: Game) => {
    const marketsForType = game?.markets?.filter((market) =>
      marketTypeSpecification.marketTypeIds.includes(market.marketTypeId),
    )

    if (marketsForType?.length > 1) {
      return getMostBalancedLineForMarkets(marketsForType)
    }

    return marketsForType ? marketsForType[0] : null
  }

  return (
    <div className="games-list-game-markets-column first-letter:flex flex-col">
      <GamesListGameMarketsColumnHeader marketTypeSpecification={marketTypeSpecification} />

      <div className="games-list-game-markets-column-items flex flex-col space-y-2">
        {games?.map((leagueGame, index) => {
          return (
            <React.Fragment key={leagueGame.id}>
              {repeatHeaderAfterNRows != 0 && index > 0 && index % repeatHeaderAfterNRows === 0 && (
                <GamesListGameMarketsColumnHeader marketTypeSpecification={marketTypeSpecification} />
              )}
              <GameListItemMarketMemo
                currency={currency}
                market={getMarket(leagueGame)}
                game={leagueGame}
                sportName={sportName}
                sportShortSign={sportShortSign}
                leagueName={leagueName}
                marketTypeSpecification={marketTypeSpecification}
              />
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

type GamesListGameMarketsProps = {
  games?: Game[]
  leagueName?: string
  sportName?: string
  sportShortSign?: string
  marketTypeSpecifications?: MarketTypeSpecification[]
  displayGamesWithNoMarkets?: boolean
  currency: Currency
  repeatHeaderAfterNRows?: number
}
const GamesListGameMarkets = ({
                                currency,
                                games,
                                leagueName,
                                sportName,
                                sportShortSign,
                                marketTypeSpecifications,
                                displayGamesWithNoMarkets,
                                repeatHeaderAfterNRows
                              }: GamesListGameMarketsProps) => {
  const { t } = useTranslation('bets')
  const _games = displayGamesWithNoMarkets ? games : games.filter((game) => game?.markets?.length > 0)

  const quickMarketsAvailable =
    _games.find((game) =>
      game.markets?.find((market) =>
        marketTypeSpecifications?.find((mts) => mts.marketTypeIds.includes(market.marketTypeId))
      )
    ) != null

  return (
    <div className={`games-list-game-markets flex overflow-hidden ${styles.gameMarketsWidth}`}>
      {quickMarketsAvailable && (
        <Swiper className="games-list-game-markets-swiper w-full" slidesPerView={'auto'} grabCursor>
          {marketTypeSpecifications?.map((marketTypeSpecification, index) => {
            let items = []
            if (games != null) {
              items = games.filter((leagueGame) =>
                leagueGame.markets?.find((market) =>
                  marketTypeSpecification.marketTypeIds.includes(market.marketTypeId)
                )
              )
            }

            return (
              <React.Fragment key={index}>
                {items.length > 0 && (
                  <SwiperSlide
                    className="games-list-game-markets-swiper-slide transition hover:bg-cardHover w-[unset]"
                    key={index}
                  >
                    <GamesListGameMarketsColumn
                      currency={currency}
                      games={_games}
                      leagueName={leagueName}
                      sportName={sportName}
                      sportShortSign={sportShortSign}
                      marketTypeSpecification={marketTypeSpecification}
                      repeatHeaderAfterNRows={repeatHeaderAfterNRows}
                    />
                  </SwiperSlide>
                )}
              </React.Fragment>
            )
          })}
        </Swiper>
      )}

      {!quickMarketsAvailable && (
        <div className="games-list-game-markets-unavailable flex flex-col space-y-2 justify-end w-full px-2">
          {_games?.map((game) => (
            <div
              className="games-list-game-markets-unavailable-item flex w-full h-[63.5px] pb-2 text-xxs"
              key={game.id}
            >
              {game.openMarketCount > 0 && (
                <div
                  className="games-list-game-markets-unavailable-item-show-all-markets flex justify-end items-end w-full h-full pr-2">
                  <Link href={buildEventUrl(game?.id, game?.name, game?.usesUsDisplay)} legacyBehavior>
                    <div className="flex items-center space-x-1 cursor-pointer">
                      <span>{t('GamesList.showAllMarkets')}</span>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </Link>
                </div>
              )}

              {game.openMarketCount === 0 && (
                <div
                  className="games-list-game-markets-unavailable-item-locked flex flex-col justify-center items-center w-full h-full bg-odd text-oddContrast rounded-[var(--odd-radius)]">
                  <FontAwesomeIcon icon={faLockKeyhole} className="text-base opacity-50" />
                  <span className="opacity-70">{t('GamesList.noMarketsAvailable')}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export type GamesListProps = {
  prematchGames?: Game[]
  liveGames?: Game[]
  outrights?: Game[]
  leagueName?: string
  leagueId?: number
  deepLinkLeague?: boolean
  sportName?: string
  sportShortSign?: string
  countryCode?: string
  iconType?: 'sport' | 'country' | 'sport-country' | 'none'
  titleType?: 'sectionName' | 'leagueName'
  highlightedTerm?: string
  isFavoritesWidget?: boolean
  onRemoveFavoriteLeague?: () => Promise<void>
  repeatHeaderRows?: boolean
  wrapperClassName?: string
  gamesListClassName?: string
  alwaysDisplayLeagueName?: boolean
  leagueUsesUsDisplay?: boolean
}
const GamesList = ({
                     prematchGames = [],
                     liveGames = [],
                     outrights = [],
                     leagueName,
                     leagueId,
                     deepLinkLeague = false,
                     sportName,
                     sportShortSign,
                     countryCode,
                     iconType,
                     titleType = 'sectionName',
                     highlightedTerm = '',
                     isFavoritesWidget = false,
                     onRemoveFavoriteLeague = null,
                     repeatHeaderRows = true,
                     wrapperClassName = '',
                     gamesListClassName = '',
                     alwaysDisplayLeagueName = false,
                     leagueUsesUsDisplay = false
                   }: GamesListProps) => {
  const { t } = useTranslation('bets')
  const currency = useUserCurrency()

  const language = useCurrentLanguage()
  const quickMarkets = useQuickMarkets(language?.id)
  const liveQuickMarkets = useLiveQuickMarkets(language?.id)

  outrights?.forEach(
    (outright) =>
      (outright.name = outright.name === 'Longterm Markets' ? t('common:Common.longtermMarkets') : outright.name)
  )

  const prematchMarketTypeSpecifications = useMemo(() => {
    let pmts: MarketTypeSpecification[] = []
    quickMarkets?.marketTypeIds?.forEach((qm) => {
      const mts = marketTypeSpecifications.find((mts) => mts.marketTypeIds.includes(qm))
      if (mts && !pmts.includes(mts)) {
        if (leagueUsesUsDisplay) mts.outcomes.sort((a, b) => a.sortKey - b.sortKey)
        pmts.push(mts)
      }
    })
    return pmts
  }, [quickMarkets])

  const liveMarketTypeSpecifications = useMemo(() => {
    let lmts: MarketTypeSpecification[] = []
    liveQuickMarkets?.marketTypeIds?.forEach((qm) => {
      const mts = marketTypeSpecifications.find((mts) => mts.marketTypeIds.includes(qm))
      if (mts && !lmts.includes(mts)) {
        if (leagueUsesUsDisplay) mts.outcomes.sort((a, b) => a.sortKey - b.sortKey)
        lmts.push(mts)
      }
    })
    return lmts
  }, [liveQuickMarkets])

  const repeatHeaderAfterNRows = useMemo(() => {
    if (repeatHeaderRows) {
      return Math.floor((window.innerHeight - 100) / 64.5) - 2
    }
    return 0
  }, [window.innerHeight])

  return (
    <div className={`games-list @container flex flex-col w-full ${wrapperClassName}`}>
      {((prematchGames?.length > 0 && liveGames?.length > 0 && titleType != 'leagueName') ||
        alwaysDisplayLeagueName) && (
        <div className="games-list-header flex overflow-hidden bg-card text-cardContrast border-b-[1px] border-neutral">
          {(leagueName || sportShortSign) && (
            <div
              className="games-list-header-inner flex items-center space-x-2 whitespace-nowrap overflow-hidden px-2 py-4 h-[40px]">
              {iconType == 'sport' && (
                <div className="games-list-header-sport-icon h-5 w-5">
                  <SportIcon sportShortSign={sportShortSign} />
                </div>
              )}

              {iconType == 'country' && (
                <div className="games-list-header-country-icon h-5 w-5">
                  <CountryIcon countryCode={countryCode} />
                </div>
              )}

              {iconType == 'sport-country' && (
                <SportCountryIcon
                  countryCode={countryCode}
                  sportShortSign={sportShortSign}
                  height="16px"
                  width="16px"
                  sportIconBackground="bg-card"
                />
              )}

              {leagueName && (
                <span
                  className="games-list-header-league-name overflow-hidden text-ellipsis text-xs uppercase font-semibold opacity-60 hover:opacity-100">
                  {deepLinkLeague && leagueId && (
                    <Link href={buildLeagueUrl(leagueId, leagueName)} legacyBehavior>
                      {leagueName}
                    </Link>
                  )}
                  {!deepLinkLeague && leagueName}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {liveGames?.length > 0 && (
        <div
          className={`games-list-live-games-wrapper flex flex-row justify-between items-end w-full py-2 bg-card text-cardContrast ${gamesListClassName}`}
        >
          <GamesListGameDetails
            games={liveGames}
            leagueName={leagueName}
            leagueId={leagueId}
            deepLinkLeague={deepLinkLeague}
            sportShortSign={sportShortSign}
            countryCode={countryCode}
            iconType={iconType}
            titleType={titleType}
            highlightedTerm={highlightedTerm}
            repeatHeaderAfterNRows={repeatHeaderAfterNRows}
            displayGamesWithNoMarkets
            isLiveSection
          />
          <GamesListGameMarkets
            currency={currency}
            games={liveGames}
            leagueName={leagueName}
            sportName={sportName}
            sportShortSign={sportShortSign}
            marketTypeSpecifications={liveMarketTypeSpecifications}
            repeatHeaderAfterNRows={repeatHeaderAfterNRows}
            displayGamesWithNoMarkets
          />
        </div>
      )}

      {prematchGames?.length > 0 && (
        <div
          className={`games-list-prematch-games-wrapper flex flex-row justify-between items-end w-full py-2 bg-card text-cardContrast ${gamesListClassName}`}
        >
          <GamesListGameDetails
            games={prematchGames}
            leagueName={leagueName}
            leagueId={leagueId}
            deepLinkLeague={deepLinkLeague}
            sportShortSign={sportShortSign}
            countryCode={countryCode}
            iconType={iconType}
            titleType={titleType}
            highlightedTerm={highlightedTerm}
            repeatHeaderAfterNRows={repeatHeaderAfterNRows}
            displayGamesWithNoMarkets
          />
          <GamesListGameMarkets
            currency={currency}
            games={prematchGames}
            leagueName={leagueName}
            sportName={sportName}
            sportShortSign={sportShortSign}
            marketTypeSpecifications={prematchMarketTypeSpecifications}
            repeatHeaderAfterNRows={repeatHeaderAfterNRows}
            displayGamesWithNoMarkets
          />
        </div>
      )}

      {outrights?.length > 0 && <OutrightsList games={outrights} currency={currency} className={gamesListClassName} />}

      {prematchGames?.length === 0 && liveGames?.length === 0 && outrights?.length === 0 && (
        <div
          className="games-list-empty flex items-center space-x-4 w-[calc(100% - 16px)] m-4 xl:m-0 p-4 bg-yellow-200 bg-opacity-30 rounded-lg overflow-hidden">
          <FontAwesomeIcon icon={faInfoCircle} className="games-list-empty-icon text-yellow-500" />
          <div className="games-list-empty-text flex flex-col space-y-2 text-sm">
            <span>{t('GamesList.noEventsAvailable')}</span>
            {isFavoritesWidget && (
              <span className="games-list-empty-favorites-text underline" onClick={onRemoveFavoriteLeague}>
                {t('GamesList.removeFromFavorites', { leagueName })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export const GamesListMemo = React.memo(GamesList, deepEqualityCheck)

export default GamesList
